import { currentUser } from "@/App";
import AppIcon from "@/assets/app-icon.svg";
import { removeContent, toggleFeatured, updateContent } from "@/services/api";
import { uploadImage } from "@/services/upload";
import {
  Cancel,
  Delete,
  Edit,
  Event,
  IosShare,
  PushPin,
  PushPinOutlined,
  Save,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CardMedia,
  Container,
  IconButton,
  List,
  ListItem,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export default function EditContent({ content, onClose }) {
  const [editing, setEditing] = useState(false);

  // Form State
  const [caption, setCaption] = useState(content?.meta?.text || "");
  const [title, setTitle] = useState(content?.meta?.title || "");
  const [lyrics, setLyrics] = useState(content?.meta?.lyrics || "");
  const [when, setWhen] = useState(content?.meta?.when || "");
  const [location, setLocation] = useState(content?.meta?.location || "");
  const [cover, setCover] = useState(content?.meta?.cover || "");
  const [price, setPrice] = useState(content?.meta?.price || "");
  const [link, setLink] = useState(content?.meta?.link || "");
  const [writers, setWriters] = useState(content?.meta?.writers || "");
  const [producers, setProducers] = useState(content?.meta?.producers || "");

  const [loading, setLoading] = useState(false);
  const [featured, setFeatured] = useState(content?.meta?.featured);
  const navigate = useNavigate();

  useEffect(() => {
    setCaption(content?.meta?.text || "");
    setTitle(content?.meta?.title || "");
    setLyrics(content?.meta?.lyrics || "");
    setWhen(content?.meta?.when || "");
    setLocation(content?.meta?.location || "");
    setCover(content?.meta?.cover || "");
    setFeatured(content?.meta?.featured);
    setPrice(content?.meta?.price || "");
    setLink(content?.meta?.link || "");
    setWriters(content?.meta?.writers || "");
    setProducers(content?.meta?.producers || "");
    // Don't auto-set editing to true, let user click Edit
  }, [content]);

  const handleCoverChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    try {
      const url = await uploadImage(file);
      setCover(url);
    } catch (error) {
      console.error("Error uploading image", error);
      alert("Error uploading image");
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setLoading(true);
    const updatedContent = {
      ...content,
      meta: {
        ...content.meta,
        text: caption,
        lyrics: lyrics,
        when: when,
        location: location,
        title: title,
        cover: cover,
        price: price,
        link: link,
        writers: writers,
        producers: producers,
      },
    };
    try {
      await updateContent(updatedContent);
      window.location.reload();
    } catch (error) {
      console.error("Error updating content", error);
      alert("Error updating content");
      setLoading(false);
    }
  };

  const handleRemoveContent = async () => {
    const result = confirm(
      "Are you sure you want to remove this content from your Notebook?",
    );
    if (result) {
      await removeContent(content);
      if (onClose) onClose();
      window.location.reload();
    }
  };

  const handleToggleFeatured = async () => {
    setLoading(true);
    await toggleFeatured(content);
    window.location.reload();
  };

  const copyLink = () => {
    const link = window.location.origin + "/" + content.owner.key;
    navigator.clipboard.writeText(link);
    alert("Link copied to clipboard.");
    if (onClose) onClose();
  };

  const isOwner =
    currentUser?.key === content.artist_key ||
    currentUser?.key === content.owner.key;

  if (editing) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ pb: 10, pt: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Button onClick={() => setEditing(false)} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <Save />}
            >
              Save
            </Button>
          </Box>

          {content.type === "experience" && (
            <>
              <TextField
                label="When"
                value={when}
                onChange={(e) => setWhen(e.target.value)}
                fullWidth
                multiline
                size="small"
                sx={{ my: 2 }}
              />
              <TextField
                label="Location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                fullWidth
                multiline
                size="small"
                sx={{ my: 2 }}
              />
            </>
          )}

          {["thread", "audio", "merch"].includes(content.type) && (
            <>
              <TextField
                id="title"
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                multiline
                size="small"
                sx={{ my: 2 }}
              />
            </>
          )}

          <TextField
            id="caption"
            label="Caption"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            fullWidth
            multiline
            size="small"
            sx={{ my: 2 }}
          />

          {content.type === "audio" && (
            <>
              <TextField
                id="writers"
                label="Written by"
                value={writers}
                onChange={(e) => setWriters(e.target.value)}
                fullWidth
                multiline
                size="small"
                sx={{ my: 2 }}
              />
              <TextField
                id="producers"
                label="Produced by"
                value={producers}
                onChange={(e) => setProducers(e.target.value)}
                fullWidth
                multiline
                size="small"
                sx={{ my: 2 }}
              />
              <TextField
                id="lyrics"
                label="Lyrics"
                value={lyrics}
                onChange={(e) => setLyrics(e.target.value)}
                fullWidth
                multiline
                rows={6}
                size="small"
                sx={{ my: 2 }}
              />
            </>
          )}

          {content.type === "merch" && (
            <>
              <TextField
                id="price"
                label="Price"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                fullWidth
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
                sx={{ my: 2 }}
              />
              <TextField
                id="link"
                label="Link"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                fullWidth
                sx={{ my: 2 }}
              />
            </>
          )}
          {["audio", "merch", "video"].includes(content.type) && (
            <Box sx={{ position: "relative", width: "fit-content" }}>
              <CardMedia
                component="img"
                src={cover ? cover : content.owner?.meta?.artwork || AppIcon}
                sx={{ my: 2, borderRadius: "20px", aspectRatio: "1/1" }}
              />
              <input
                accept="image/*"
                style={{ display: "none" }}
                id="cover"
                type="file"
                onChange={handleCoverChange}
              />
              <IconButton
                onClick={() => document.getElementById("cover").click()}
                sx={{
                  position: "absolute",
                  bottom: 26,
                  right: 10,
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.7)",
                  },
                }}
                disabled={loading}
              >
                <Edit sx={{ color: "white" }} />
              </IconButton>
            </Box>
          )}
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box style={{ minHeight: "50vh" }} sx={{ pb: 10 }}>
        {content.type === "experience" && isOwner && (
          <Box sx={{ px: 2 }}>
            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              sx={{ my: 4, borderRadius: "30px" }}
              startIcon={<Event />}
              onClick={() => navigate(`/experience/${content.key}`)}
            >
              Manage Experience
            </Button>
          </Box>
        )}

        <List>
          {isOwner && (
            <ListItem button onClick={handleRemoveContent} sx={{ mb: 2 }}>
              <Delete sx={{ mr: 2 }} color="info" />
              <Typography>Remove Content</Typography>
            </ListItem>
          )}
          {isOwner && currentUser.role === "artist" && (
            <>
              <ListItem button onClick={() => setEditing(true)} sx={{ mb: 2 }}>
                <Edit sx={{ mr: 2 }} color="info" />
                <Typography>Edit Content</Typography>
              </ListItem>

              {["video", "audio", "image", "merch"].includes(content.type) && (
                <ListItem button onClick={handleToggleFeatured} sx={{ mb: 2 }}>
                  {featured ? (
                    <PushPin color="secondary" sx={{ mr: 2 }} />
                  ) : (
                    <PushPinOutlined sx={{ color: "darkgray", mr: 2 }} />
                  )}
                  <Typography>
                    {featured ? "Remove from Featured" : "Pin to Featured"}
                  </Typography>
                </ListItem>
              )}
            </>
          )}
          <ListItem button onClick={copyLink} sx={{ mb: 2 }}>
            <IosShare sx={{ mr: 2 }} color="info" />
            <Typography>Share</Typography>
          </ListItem>
        </List>
      </Box>
    </Container>
  );
}
